import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  WarningAmber,
  InsertDriveFileOutlined,
  CheckCircleOutline,
  ErrorOutline,
  PanToolOutlined,
  Send
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useGetSpecAuditSessionQuery } from "../../data/api/queries/knowledgeAgent";
import { useResumeSpecAuditSessionMutation } from "../../data/api/commands/knowledgeAgentCommands";
import { Loading } from "modules/_shared/pages";
import "./AgentSessionView.scss";

const AgentSessionView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();

  const {
    data: specAuditSession,
    isLoading,
    isFetching,
    refetch: refetchSpecAuditSession
  } = useGetSpecAuditSessionQuery(sessionId!, { skip: !sessionId });

  const [resumeSpecAuditSession] = useResumeSpecAuditSessionMutation();
  const [hitlResponse, setHitlResponse] = useState("");
  const [isSubmittingHitl, setIsSubmittingHitl] = useState(false);
  const [hitlSubmitted, setHitlSubmitted] = useState(false);

  const handleHitlSubmit = async () => {
    if (!hitlResponse.trim() || !sessionId) return;
    setIsSubmittingHitl(true);
    try {
      await resumeSpecAuditSession({
        session_id: sessionId,
        answers: [hitlResponse.trim()]
      }).unwrap();
      setHitlResponse("");
      setHitlSubmitted(true);
      refetchSpecAuditSession();
    } catch (error) {
      console.error("HITL submission failed:", error);
    } finally {
      setIsSubmittingHitl(false);
    }
  };

  const totalParamCount = specAuditSession?.responseJson
    ? specAuditSession?.responseJson?.ok_count +
      specAuditSession?.responseJson?.under_count +
      specAuditSession?.responseJson?.over_count +
      specAuditSession?.responseJson?.in_review_count
    : 0;

  if (isLoading) {
    return (
      <div className="agent-session-view-container">
        <nav className="agent-breadcrumb">
          <Link to="/home" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">&gt;</span>
          <Link to="/knowledge-agent/all-details" className="breadcrumb-link">
            Knowledge Agents
          </Link>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">Session Details</span>
        </nav>
        <div className="loading-container">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="agent-session-view-container">
      {/* Breadcrumb */}
      <nav className="agent-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Home
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/knowledge-agent/all-details" className="breadcrumb-link">
          Knowledge Agents
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Session Details</span>
      </nav>

      {/* Page Header */}
      <div className="agent-page-header">
        <div className="agent-page-title-row">
          <h1 className="agent-page-title">
            {/* Session: {specAuditSession?.sessionId || sessionId} */}
            {specAuditSession?.userQuery}
          </h1>
          {specAuditSession?.status && (
            <span className={`status-badge status-${specAuditSession?.status}`}>
              {specAuditSession?.status?.toLowerCase() === "running" && (
                <CircularProgress size={14} thickness={5} color="inherit" />
              )}
              {specAuditSession?.status?.toLowerCase() === "hitl_required" && (
                <PanToolOutlined sx={{ fontSize: 14 }} />
              )}
              {specAuditSession?.status?.toLowerCase() === "complete" && (
                <CheckCircleOutline sx={{ fontSize: 14 }} />
              )}
              {specAuditSession?.status?.toLowerCase() === "error" && (
                <ErrorOutline sx={{ fontSize: 14 }} />
              )}
              {specAuditSession?.status?.charAt(0)?.toUpperCase() +
                specAuditSession?.status?.slice(1)}
            </span>
          )}
        </div>

        {specAuditSession?.engDocId && (
          <p className="agent-page-description">
            <strong>Engineering Document:</strong>{" "}
            {specAuditSession?.engDocName || specAuditSession?.engDocId}
          </p>
        )}

        {specAuditSession?.standardIds && (
          <p className="agent-page-description">
            <strong>Reference Standards:</strong>{" "}
            {specAuditSession?.standardDocName?.join(", ") ||
              specAuditSession?.standardIds?.join(", ")}
          </p>
        )}

        {/* {specAuditSession?.userQuery && (
          <p className="agent-page-description">
            <strong>Query:</strong> {specAuditSession.userQuery}
          </p>
        )} */}
        {/* <div className="session-meta"> */}
        {/* {specAuditSession?.status && (
            <span className={`status-badge status-${specAuditSession.status}`}>
              {specAuditSession.status.charAt(0).toUpperCase() +
                specAuditSession.status.slice(1)}
            </span>
          )} */}
        {/* {specAuditSession?.createdAt && (
            <span className="meta-item">
              Created:{" "}
              {new Date(specAuditSession.createdAt).toLocaleDateString(
                "en-GB",
                {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric"
                }
              )}
            </span>
          )}
          {specAuditSession?.createdBy && (
            <span className="meta-item">By: {specAuditSession.createdBy}</span>
          )} */}
        {/* </div> */}
      </div>

      {/* Analysis Results */}
      <div className="session-results">
        {isFetching ? (
          <div className="analysis-loading">
            <CircularProgress size={40} />
            <p>Loading session data...</p>
          </div>
        ) : specAuditSession?.status === "complete" &&
          specAuditSession.responseJson ? (
          <>
            {/* Context Card */}
            <div className="analysis-result context-card">
              <h4 className="section-title">Context</h4>
              <ul className="analysis-points">
                {specAuditSession?.responseJson?.context_card?.asset && (
                  <li>
                    <strong>Asset:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.asset}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.system && (
                  <li>
                    <strong>System:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.system}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.subsystem && (
                  <li>
                    <strong>Subsystem:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.subsystem}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.equipment_tag && (
                  <li>
                    <strong>Equipment Tag:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.equipment_tag
                    }
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.equipment_type && (
                  <li>
                    <strong>Equipment Type:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.equipment_type
                    }
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.operating_intent && (
                  <li>
                    <strong>Operating Intent:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.operating_intent
                    }
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.environment && (
                  <li>
                    <strong>Environment:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.environment}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.location && (
                  <li>
                    <strong>Location:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.location}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.equipment_service_life_years != null && (
                  <li>
                    <strong>Equipment Service Life:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.equipment_service_life_years
                    }{" "}
                    years
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.fpso_on_station_life_years != null && (
                  <li>
                    <strong>FPSO On-Station Life:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.fpso_on_station_life_years
                    }{" "}
                    years
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.mooring_type && (
                  <li>
                    <strong>Mooring Type:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.mooring_type}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.disconnection_policy && (
                  <li>
                    <strong>Disconnection Policy:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.disconnection_policy
                    }
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card
                  ?.topside_concept && (
                  <li>
                    <strong>Topside Concept:</strong>{" "}
                    {
                      specAuditSession?.responseJson?.context_card
                        ?.topside_concept
                    }
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.gas_roles
                  ?.length > 0 && (
                  <li>
                    <strong>Gas Roles:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.gas_roles?.join(
                      ", "
                    )}
                  </li>
                )}
                {specAuditSession?.responseJson?.context_card?.future_provisions
                  ?.length > 0 && (
                  <li>
                    <strong>Future Provisions:</strong>{" "}
                    {specAuditSession?.responseJson?.context_card?.future_provisions?.join(
                      ", "
                    )}
                  </li>
                )}
              </ul>

              {/* Raw Context Chunks */}
              {/* {specAuditSession.responseJson.context_card.raw_context_chunks
                .length > 0 && (
                <div className="raw-context-section">
                  <h5 className="raw-context-title">Raw Context Chunks:</h5>
                  <ul className="raw-context-list">
                    {specAuditSession.responseJson.context_card.raw_context_chunks.map(
                      (chunk, idx) => (
                        <li key={idx}>{chunk}</li>
                      )
                    )}
                  </ul>
                </div>
              )} */}
            </div>

            {/* Summary Counts */}
            <div className="analysis-result summary-counts">
              <h4 className="section-title">Summary</h4>
              <div className="counts-grid">
                <div className="count-item total">
                  <span className="count-value">
                    {totalParamCount || "No data"}
                  </span>
                  <span className="count-label">Total</span>
                </div>
                <div className="count-item ok">
                  <span className="count-value">
                    {specAuditSession?.responseJson?.ok_count}
                  </span>
                  <span className="count-label">OK</span>
                </div>
                <div className="count-item under">
                  <span className="count-value">
                    {specAuditSession?.responseJson?.under_count}
                  </span>
                  <span className="count-label">Under</span>
                </div>
                <div className="count-item over">
                  <span className="count-value">
                    {specAuditSession?.responseJson?.over_count}
                  </span>
                  <span className="count-label">Over</span>
                </div>
                <div className="count-item in-review">
                  <span className="count-value">
                    {specAuditSession?.responseJson?.in_review_count}
                  </span>
                  <span className="count-label">In Review</span>
                </div>
              </div>
            </div>

            {/* Parameter Findings (Non-OK) */}
            {specAuditSession?.responseJson?.parameter_table
              ?.filter((param) => param.final_verdict !== "OK")
              ?.map((param) => (
                <div
                  key={param.param_name}
                  className={`risk-alert ${
                    param.context_severity === "HIGH"
                      ? "high-risk"
                      : param.context_severity === "MEDIUM"
                      ? "medium-risk"
                      : "low-risk"
                  }`}
                >
                  <div className="risk-alert-header">
                    <WarningAmber className="risk-icon" />
                    <span className="risk-title">
                      {param.param_display} — {param.final_verdict} (
                      {param.context_severity} Severity)
                    </span>
                  </div>
                  <div className="risk-alert-body">
                    <p className="risk-description">{param.explanation}</p>
                    <div className="param-values">
                      <span className="param-value spec">
                        <strong>Spec:</strong> {param.spec_value_str}
                      </span>
                      <span className="param-value standard">
                        <strong>Standard:</strong> {param.standard_value_str}
                      </span>
                      <span className="param-value action-badge">
                        Action: {param.action}
                      </span>
                      <span className="param-value standard-clause">
                        <InsertDriveFileOutlined
                          style={{ fontSize: 14, marginRight: 4 }}
                        />
                        Standard Clause: {param.standard_clause}
                      </span>
                    </div>
                    {specAuditSession?.responseJson?.explanations?.[
                      param.param_name
                    ] && (
                      <div className="explanation-detail">
                        <strong>Explanation:</strong>{" "}
                        {
                          specAuditSession?.responseJson?.explanations?.[
                            param.param_name
                          ]
                        }
                      </div>
                    )}
                    {specAuditSession?.responseJson?.risks?.[
                      param.param_name
                    ] && (
                      <div className="risk-detail">
                        <strong>Risk:</strong>{" "}
                        {
                          specAuditSession?.responseJson?.risks?.[
                            param.param_name
                          ]
                        }
                      </div>
                    )}
                    {specAuditSession?.responseJson?.actions?.find(
                      (a) => a.param_name === param.param_name
                    )?.recommended_next_step && (
                      <div className="action-detail">
                        <strong>Recommended Action:</strong>{" "}
                        {
                          specAuditSession?.responseJson?.actions?.find(
                            (a) => a.param_name === param.param_name
                          )?.recommended_next_step
                        }
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {/* OK Parameters */}
            {specAuditSession?.responseJson?.ok_count > 0 && (
              <div className="analysis-result ok-params">
                <h4 className="section-title ok-title">Compliant Parameters</h4>
                {specAuditSession?.responseJson?.parameter_table
                  ?.filter((param) => param.final_verdict === "OK")
                  ?.map((param) => (
                    <div key={param.param_name} className="ok-param-item">
                      <div className="ok-param-header">
                        <span className="ok-badge">OK</span>
                        <strong>{param.param_display}</strong>
                      </div>
                      <p className="ok-param-explanation">
                        {specAuditSession?.responseJson?.explanations?.[
                          param.param_name
                        ] || param.explanation}
                      </p>
                      <div className="param-values">
                        <span className="param-value spec">
                          <strong>Spec:</strong> {param.spec_value_str}
                        </span>
                        <span className="param-value standard">
                          <strong>Standard:</strong> {param.standard_value_str}
                        </span>
                      </div>
                      {specAuditSession?.responseJson?.risks?.[
                        param.param_name
                      ] && (
                        <p className="ok-param-risk">
                          <strong>Note:</strong>{" "}
                          {
                            specAuditSession?.responseJson?.risks?.[
                              param.param_name
                            ]
                          }
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </>
        ) : specAuditSession?.status === "error" ? (
          <div className="analysis-error">
            <WarningAmber className="error-icon" />
            <p>
              Error in analysis:{" "}
              {specAuditSession?.errorMessage || "Unknown error occurred."}
            </p>
          </div>
        ) : specAuditSession?.status === "hitl_required" &&
          specAuditSession.hitlQuestions &&
          !hitlSubmitted ? (
          <div className="hitl-clarification-section">
            <div className="hitl-card">
              <div className="hitl-card-header">
                <span className="hitl-icon">&#9432;</span>
                <span className="hitl-title">AGENT CLARIFICATION REQUEST</span>
                <span className="hitl-badge">
                  {Array.isArray(specAuditSession.hitlQuestions)
                    ? specAuditSession.hitlQuestions.length
                    : 0}{" "}
                  {Array.isArray(specAuditSession.hitlQuestions) &&
                  specAuditSession.hitlQuestions.length === 1
                    ? "Question"
                    : "Questions"}
                </span>
              </div>
              <div className="hitl-card-body">
                <p className="hitl-description">
                  To refine the compliance assessment, I need a few
                  clarifications. Your answers will help me re-evaluate the
                  verdict with greater precision.
                </p>
                <div className="hitl-questions-list">
                  {(Array.isArray(specAuditSession.hitlQuestions)
                    ? specAuditSession.hitlQuestions
                    : []
                  ).map(
                    (
                      item: { question: string; context: string },
                      idx: number
                    ) => (
                      <div key={idx} className="hitl-question-card">
                        <div className="hitl-question-content">
                          <p className="hitl-question-text">{item.question}</p>
                          {item.context && (
                            <div className="hitl-question-context">
                              <span className="context-label">
                                Why this matters:
                              </span>
                              <p className="context-text">{item.context}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className="hitl-response-input-section">
              <div className="hitl-input-label">
                <Send style={{ fontSize: 14, color: "#656579" }} />
                <span>Provide your answers below to continue the analysis</span>
              </div>
              <div className="hitl-input-row">
                <textarea
                  className="hitl-response-input"
                  placeholder="Type your response here... (e.g., Yes, a sour-service assessment has been conducted confirming H2S levels support 24 HRC)"
                  value={hitlResponse}
                  onChange={(e) => setHitlResponse(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleHitlSubmit();
                    }
                  }}
                  disabled={isSubmittingHitl}
                  rows={3}
                />
                <button
                  className="hitl-send-btn"
                  onClick={handleHitlSubmit}
                  disabled={!hitlResponse.trim() || isSubmittingHitl}
                  title="Send response"
                >
                  {isSubmittingHitl ? (
                    <CircularProgress size={18} style={{ color: "#fff" }} />
                  ) : (
                    <Send className="hitl-send-icon" />
                  )}
                </button>
              </div>
              <p className="hitl-input-hint">
                Press Enter to send · Shift+Enter for new line
              </p>
            </div>
          </div>
        ) : specAuditSession?.status &&
          specAuditSession.status !== "complete" ? (
          <div className="analysis-loading">
            <CircularProgress size={40} />
            <p>
              Analysis in progress...
              {/* (Status: {specAuditSession.status}) */}
            </p>
          </div>
        ) : (
          <div className="analysis-empty">
            <p>No analysis data available for this session.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentSessionView;
